import React, { useEffect, useState } from "react";
import { getData, postData, postFormData } from '../middleware/apiMiddleware';
import s from './ListeningScriptGeneration.module.css';
import { useNavigate } from "react-router-dom";
import { VoiceListJunior, VoiceListSenior } from '../middleware/MyConstant';
import { SoundPlayer } from './Utilities/SoundPlayer.js';
import KeyListeningFile from './KeyListeningFile.js';

export default function ListeningScriptGeneration(prop) {	
  const navigate = useNavigate();
  const itemId = prop.itemId;
  const itemType = prop.itemType || 74;

  // 新增：添加 booster_expression 状态管理
  const [boosterExpression, setBoosterExpression] = useState('');
  // 新增：booster 更新加载状态（可选，提升用户体验）
  const [boosterUpdating, setBoosterUpdating] = useState(false);

  const [stText, setStText] = useState('');
  const [ndText, setNdText] = useState('');
  const [extractedNames, setExtractedNames] = useState([]);
  const [voiceActorSelections, setVoiceActorSelections] = useState({});
  const [processingLoading, setProcessingLoading] = useState(false);
  const [defaultSpeakerVoices, setDefaultSpeakerVoices] = useState({});
  const [srtPath, setSrtPath] = useState('');
  const [srtHkPath, setSrtHkPath] = useState('');
  const [audioPath, setAudioPath] = useState('');
  const [srtNdPath, setSrtNdPath] = useState('');
  const [srtNdHkPath, setSrtNdHkPath] = useState('');
  const [audioNdPath, setAudioNdPath] = useState('');
  const [mainMsg, setMainMsg] = useState('');
  const [html, setHtml] = useState('');
  const [correctionPrompt, setCorrectionPrompt] = useState('');
  const [htmlUpdating, setHtmlUpdating] = useState(false);
  const [generatingCorrection, setGeneratingCorrection] = useState(false);


  const [topic, setTopic] = useState('');
  const [title, setTitle] = useState('');
  const [textType, setTextType] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [schoolLevel, setSchoolLevel] = useState('');
  const [schoolLevelUpdating, setSchoolLevelUpdating] = useState(false);

  const currentVoiceList = schoolLevel === 'junior' 
    ? VoiceListJunior 
    : (schoolLevel === 'senior' ? VoiceListSenior : []);
  const extractNamesFromScript = (scriptContent) => {
    if (!scriptContent.trim()) return [];
    const lines = scriptContent.split('\n').filter(line => line.trim() !== '');
    const rawNames = lines.map(line => {
      const nameMatch = line.match(/^([^:]+):/);
      if (!nameMatch) return '';
      let cleanName = nameMatch[1].trim();
      cleanName = cleanName.replace(/\s*\([^)]*\)/g, '').trim();
      return cleanName;
    }).filter(name => name !== '');
    return [...new Set(rawNames)];
  };

const fetchDefaultSpeakerVoices = async () => {
  // 新增：空值校验 - 无schoolLevel时不调用接口（避免无效请求）
  if (!schoolLevel) return;

  try {
    // 修改：拼接schoolLevel查询参数到URL
    const res = await getData(`listeningApi/getListeningSpeaker?schoolLevel=${schoolLevel}`, navigate);
    const { speaker = [] } = res.data || {};
    const defaultVoiceMap = {};
    speaker.forEach(item => {
      if (item.speaker && item.voice) {
        defaultVoiceMap[item.speaker] = item.voice;
      }
    });
    setDefaultSpeakerVoices(defaultVoiceMap);
  } catch (error) {
    console.error("Fetch default speaker voices failed:", error);
    alert("Load default speaker voices failed, please retry.");
  }
};

  const updateSchoolLevel = async (newSchoolLevel) => {
    if (!newSchoolLevel || newSchoolLevel === schoolLevel) return;
    if (schoolLevelUpdating || processingLoading) return;

    try {
      setSchoolLevelUpdating(true);
      await postData(
        "listeningApi/updateSchoolLevel",
        { itemId, schoolLevel: newSchoolLevel },
        navigate
      );
      setSchoolLevel(newSchoolLevel);
    } catch (error) {
      console.error("Update school level failed:", error);
      alert("Failed to update school level, please retry.");
    } finally {
      setSchoolLevelUpdating(false);
    }
  };
const handleSchoolLevelToggle = () => {
  // 根据当前值计算目标值
  const targetLevel = schoolLevel === 'junior' ? 'senior' : 'junior';
  // 调用原有更新接口的函数
  updateSchoolLevel(targetLevel);
};
  const generalItemUpload = async (selectedFile) => {
    try {
      const data = new FormData();
      data.append("itemId", itemId);
      data.append("generalfile", selectedFile);
      let res = await postFormData("utility/generalItemUpload", data, navigate);
      return res.data;
    } catch (err) {
      console.error("General item upload failed:", err);
      alert("File upload failed, please retry.");
      return null;
    }
  };

  const extractFileName = (url) => {
    if (!url) return '';
    const pathname = new URL(url).pathname;
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
    return fileName;
  };

  // SRT 辅助函数：获取内容 & 解析纯文本
  const fetchSrtContent = async (srtUrl) => {
    try {
      const response = await fetch(srtUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch SRT: ${response.statusText}`);
      }
      const srtContent = await response.text();
      return srtContent;
    } catch (error) {
      console.error("Fetch SRT content failed:", error);
      alert("Failed to load SRT file, please retry.");
      return null;
    }
  };

  const parseSrtToPlainText = (srtContent, fillAnnouncer) => {
    if (!srtContent) return '';
    const srtLines = srtContent.split('\n').map(line => line.trim());
    let plainText = [];
    let isContentLine = false;

    for (const line of srtLines) {
      if (!line) {
        isContentLine = false;
        continue;
      }
      if (/^\d+$/.test(line)) {
        isContentLine = false;
        continue;
      }
      if (line.includes('-->')) {
        isContentLine = true;
        continue;
      }
      if (isContentLine) {
        plainText.push(fillAnnouncer ? `Announcer: ${line}` : line);
      }
    }

    const result = plainText.join('\n');
    return result;
  };

  // 提取 SRT 文本填充到对应 textarea
  const extractTextFromCurrentSrt = async () => {
    if (processingLoading) return;

    try {
      if (mainMsg) {
        const exceptionDotPatterns = [
          /\b(Mr|Mrs|Ms|Mx|Dr|Prof|Gen|Rep|Sen|St|Rev|Fr)\./g,
          /\b(e\.g|i\.e|vs|cf|et al)\./g,
          /\b(Inc|Ltd|Co|Corp|LLC|Pty|Plc)\./g,
          /\b([A-Z])\.(?=\s?[A-Z]\.|\s)/g,
          /\b(a\.m|p\.m)\./g,
          /\b(Jan|Feb|Mar|Apr|Aug|Sept|Oct|Nov|Dec|Mon|Tue|Wed|Thu|Fri|Sat|Sun)\./g,
          /\b(St|Ave|Rd|Blvd|Mt)\./g,
          /(\d)\.(?=\d)/g,
          /\.(com|org|net|co\.uk|jpg|png|exe|pdf|email|gov)\b/g,
          /(\w)\.(?=\w+@|\w+\.)/g,
          /\.\.\./g
        ];

        const dotPlaceholder = "__DOT_PLACEHOLDER__";
        let protectedMsg = mainMsg;
        exceptionDotPatterns.forEach(pattern => {
          protectedMsg = protectedMsg.replace(pattern, match => {
            return match.replace(/\./g, dotPlaceholder);
          });
        });

        let extractedMainMsg = protectedMsg
          .split('.')
          .filter(sentence => sentence.trim() !== '')
          .map(sentence => {
            const restoredSentence = sentence.trim().replace(new RegExp(dotPlaceholder, 'g'), '.');
            return `Announcer: ${restoredSentence}.`;
          })
          .join('\n');

        extractedMainMsg = extractedMainMsg.replace(new RegExp(dotPlaceholder, 'g'), '.');
        setStText(extractedMainMsg);
      }

      if (srtNdPath && (itemType == 74 || itemType == 96 || itemType == 97)) {
        const srtNdRes = await postData(
          "listeningApi/extractSrtText",
          { srtUrl: srtNdPath, fillAnnouncer: false },
          navigate
        );
        if (srtNdRes.data === "success" && srtNdRes.extractedText) {
          setNdText(srtNdRes.extractedText);
        } else {
          alert("提取第二个 SRT 文本失败：" + srtNdRes.msg);
        }
      }

    } catch (error) {
      console.error("Extract text from SRT failed:", error);
      alert("Failed to extract text from SRT, please retry.");
    }
  };

  const breakCurrentText = async () => {
    let text = ndText;
    const exceptionDotPatterns = [
      /\b(Mr|Mrs|Ms|Mx|Dr|Prof|Gen|Rep|Sen|St|Rev|Fr)\./g,
      /\b(e\.g|i\.e|vs|cf|et al)\./g,
      /\b(Inc|Ltd|Co|Corp|LLC|Pty|Plc)\./g,
      /\b([A-Z])\.(?=\s?[A-Z]\.|\s)/g,
      /\b(a\.m|p\.m)\./g,
      /\b(Jan|Feb|Mar|Apr|Aug|Sept|Oct|Nov|Dec|Mon|Tue|Wed|Thu|Fri|Sat|Sun)\./g,
      /\b(St|Ave|Rd|Blvd|Mt)\./g,
      /(\d)\.(?=\d)/g,
      /\.(com|org|net|co\.uk|jpg|png|exe|pdf|email|gov)\b/g,
      /(\w)\.(?=\w+@|\w+\.)/g,
      /\.\.\./g
    ];

    const dotPlaceholder = "__DOT_PLACEHOLDER__";
    let protectedText = text;
    exceptionDotPatterns.forEach(pattern => {
      protectedText = protectedText.replace(pattern, match => {
        return match.replace(/\./g, dotPlaceholder);
      });
    });

    const sentenceEndPattern = /([.!?])(?=\s|$)/g;
    const textWithSeparator = protectedText.replace(sentenceEndPattern, "$1###SEP###");
    const sentenceFragments = textWithSeparator.split("###SEP###")
      .filter(fragment => fragment.trim() !== "");

    let activeName = null;
    const processedSentences = [];

    for (const fragment of sentenceFragments) {
      let trimmedFragment = fragment.trim();
      if (!trimmedFragment) continue;

      let restoredFragment = trimmedFragment.replace(new RegExp(dotPlaceholder, 'g'), '.');
      const namePattern = /^([A-Za-z. ]+):\s*/;
      const nameMatch = restoredFragment.match(namePattern);

      if (nameMatch) {
        activeName = nameMatch[1].trim();
        const contentAfterName = restoredFragment.replace(namePattern, "").trim();
        const fullSentence = `${activeName}: ${contentAfterName}`;
        processedSentences.push(fullSentence);
      } else {
        if (activeName) {
          const fullSentence = `${activeName}: ${restoredFragment}`;
          processedSentences.push(fullSentence);
        } else {
          processedSentences.push(restoredFragment);
        }
      }
    }

    const finalResult = processedSentences.join('\n');
    setNdText(finalResult);
  };

  // 新增：更新 booster_expression 的核心函数
  const updateBoosterExpression = async () => {
    // 空值校验
    if (!boosterExpression.trim()) {
      alert("Booster expression cannot be empty!");
      return;
    }
    if (boosterUpdating) return;

    try {
      setBoosterUpdating(true);
      // 调用更新接口：参数包含 itemId（必传，定位资源）和 boosterExpression
      await postData(
        "listeningApi/updateListeningBoosterExpression",
        { itemId, boosterExpression }, // 若接口确实需要 itemType，可添加 itemType
        navigate
      );
    } catch (error) {
      console.error("Update booster expression failed:", error);
      alert("Failed to update booster expression, please retry.");
    } finally {
      setBoosterUpdating(false);
    }
  };

  const processBoosterExpression = async () => {
    let obj = { itemId, boosterExpression};
    await postData("listeningApi/processListeningBoosterExpression", obj, navigate);
    setAudioPath("https://ehla-media-bucket.s3.amazonaws.com/" + audioPath);
    
  }


  const updateKeyListeningAudioPath = async (audioPath) => {
    let obj = { itemId, audioPath};
    await postData("item/updateKeyListening", obj, navigate);
    setAudioPath("https://ehla-media-bucket.s3.amazonaws.com/" + audioPath);
  };

  const updateKeyListeningAudioNdPath = async (audioPath) => {
    let obj = { itemId, audioNdPath: audioPath};
    await postData("item/updateKeyListening", obj, navigate);
    setAudioNdPath("https://ehla-media-bucket.s3.amazonaws.com/" + audioPath);
  };

  const updateKeyListeningSrtPath = async (srtPath) => {
    let obj = { itemId, srtPath};
    await postData("item/updateKeyListening", obj, navigate);		
    setSrtPath("https://ehla-media-bucket.s3.amazonaws.com/" + srtPath);
  };

  const updateKeyListeningSrtHkPath = async (srtPath) => {
    let obj = { itemId, srtHkPath:srtPath};
    await postData("item/updateKeyListeningHk", obj, navigate);		
    setSrtHkPath("https://ehla-media-bucket.s3.amazonaws.com/" + srtPath);
  };

  const updateKeyListeningSrtNdHkPath = async (srtPath) => {
    let obj = { itemId, srtNdHkPath:srtPath};
    await postData("item/updateKeyListeningHk", obj, navigate);		
    setSrtNdHkPath("https://ehla-media-bucket.s3.amazonaws.com/" + srtPath);
  };

  const updateKeyListeningSrtNdPath = async (srtPath) => {
    let obj = { itemId, srtNdPath:srtPath};
    await postData("item/updateKeyListening", obj, navigate);		
    setSrtNdPath("https://ehla-media-bucket.s3.amazonaws.com/" + srtPath);
  };

  const removeKeyListeningAudioPath = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningAudioPath", { itemId }, navigate);
      setAudioPath('');
    } catch (error) {
      console.error("Failed to remove AudioPath:", error);
      alert("Failed to remove audio file, please retry.");
    }
  };

  const removeKeyListeningSrtPath = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningSrtPath", { itemId }, navigate);
      setSrtPath('');
    } catch (error) {
      console.error("Failed to remove SrtPath:", error);
      alert("Failed to remove SRT file, please retry.");
    }
  };

  const removeKeyListeningSrtHk = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningSrtHk", { itemId }, navigate);
      setSrtHkPath('');
    } catch (error) {
      console.error("Failed to remove SrtHk:", error);
      alert("Failed to remove HK SRT file, please retry.");
    }
  };

  const removeKeyListeningAudioNdPath = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningAudioNdPath", { itemId }, navigate);
      setAudioNdPath('');
    } catch (error) {
      console.error("Failed to remove AudioNdPath:", error);
      alert("Failed to remove second part audio file, please retry.");
    }
  };

  const removeKeyListeningSrtNdPath = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningSrtNdPath", { itemId }, navigate);
      setSrtNdPath('');
    } catch (error) {
      console.error("Failed to remove SrtNdPath:", error);
      alert("Failed to remove second part SRT file, please retry.");
    }
  };

  const removeKeyListeningSrtHkNd = async () => {
    if (processingLoading) return;
    try {
      await postData("item/removeKeyListeningSrtHkNd", { itemId }, navigate);
      setSrtNdHkPath('');
    } catch (error) {
      console.error("Failed to remove SrtHkNd:", error);
      alert("Failed to remove second part HK SRT file, please retry.");
    }
  };

  const convertSrt = async () => {
    try {
      let obj = { 'itemId' : itemId };
      await postData("item/processListeningHkSrt", obj, navigate); 
      await fetchData();
    } catch (error) {
      console.error("Convert Srt failed:", error);
      alert("Convert Srt into HK failed, please retry.");
    }
  };
//kl.expression, kl.topic, kl.title, kl.text_type, kl.question, kl.answer
  const fetchData = async () => {
    try {
      const scriptRes = await getData(`listeningApi/getListeningScriptData?itemId=${itemId}`, navigate);
      const { 
        rawScriptSt = '', 
        rawScriptNd = '', 
        mainMsg = '', 
        voiceActors = [],
        html = '',
        correction_prompt = '',
        expression = '',
        topic = '',
        title = '',
        textType = '',
        question = '',
        answer = '',
        schoolLevel = '',
      } = scriptRes.data || {};
      setStText(rawScriptSt);
      setNdText(rawScriptNd);
      setMainMsg(mainMsg);
      setHtml(html);
      setCorrectionPrompt(correction_prompt);
      setTopic(topic);
      setTitle(title);
      setTextType(textType);
      setQuestion(question);
      setAnswer(answer);
      setBoosterExpression(expression);
      setSchoolLevel(schoolLevel);
      const uniqueNames = voiceActors.length > 0 
        ? [...new Set(voiceActors.map(item => item.speaker))] 
        : [];
      setExtractedNames(uniqueNames);
      const voiceSelections = {};
      uniqueNames.forEach(speaker => {
        const matchedItem = voiceActors.find(item => item.speaker === speaker);
        voiceSelections[speaker] = matchedItem ? matchedItem.voice : '';
      });
      setVoiceActorSelections(voiceSelections);

      const listeningRes = await getData('item/getListeningByItemId?itemId=' + itemId, navigate);
      const resData = listeningRes.data || {};
      if (resData.audioPath) { setAudioPath(resData.audioPath); }
      if (resData.srtPath) { setSrtPath(resData.srtPath); }
      if (resData.srtPathJson?.hk) { setSrtHkPath(resData.srtPathJson.hk); }
      if (resData.audioNdPath) { setAudioNdPath(resData.audioNdPath); }
      if (resData.srtNdPath) { setSrtNdPath(resData.srtNdPath); }
      if (resData.srtPathJson?.nd_hk) { setSrtNdHkPath(resData.srtPathJson.nd_hk); }
    } catch (error) {
      console.error("Fetch data failed:", error);
      alert("Load data failed, please retry.");
    }
  };

const extractName = async () => {
  if (processingLoading) return;

  let tmpStText = stText;

  if (!tmpStText) {
    tmpStText = `Announcer: ${mainMsg}`;
    setStText(tmpStText);
  }
      

  try {
    await postData(
      "listeningApi/updateListeningRawScript",
      { itemId, rawScriptSt: tmpStText, rawScriptNd: ndText },
      navigate
    );
  } catch (error) {
    console.error("Failed to save listening text:", error);
    alert("Failed to save text, please retry first.");
    return;
  }

  const combinedText = `${tmpStText}\n${ndText}`;
  const uniqueNames = extractNamesFromScript(combinedText);
  setExtractedNames(uniqueNames);
  const initialSelections = {};
  uniqueNames.forEach(name => {
    // 这里会自动使用最新的defaultSpeakerVoices（学段切换后已更新）
    initialSelections[name] = defaultSpeakerVoices[name] || '';
  });
  setVoiceActorSelections(initialSelections);
};

  const handleVoiceActorChange = (name, selectedVoice) => {
    setVoiceActorSelections(prev => ({ ...prev, [name]: selectedVoice }));
  };

  const handleRemoveSpeaker = async (speakerToRemove) => {
    if (processingLoading) return;
    
    if (!window.confirm(`Are you sure to remove "${speakerToRemove}"?`)) {
      return;
    }

    try {
      await postData(
        "listeningApi/removeVoiceActor",
        { itemId, speaker: speakerToRemove },
        navigate
      );

      const updatedNames = extractedNames.filter(name => name !== speakerToRemove);
      setExtractedNames(updatedNames);
      const updatedSelections = { ...voiceActorSelections };
      delete updatedSelections[speakerToRemove];
      setVoiceActorSelections(updatedSelections);

    } catch (error) {
      console.error("Failed to remove speaker:", error);
      alert(`Failed to remove "${speakerToRemove}", please retry.`);
    }
  };

  const isAllSelected = () => {
    if (extractedNames.length === 0) return false;
    return extractedNames.every(name => voiceActorSelections[name]?.trim() !== '');
  };

  const updateVoiceActor = async () => {
    const voiceActors = Object.entries(voiceActorSelections).map(([speaker, voice]) => ({
      speaker,
      voice
    }));

    try {
      const combinedText = `${stText}\n${ndText}`;
      await postData("listeningApi/updateRawScript", { itemId, rawScript: combinedText }, navigate);
      await postData("listeningApi/updateVoiceActor", { itemId, voiceActors }, navigate);
    } catch (error) {
      console.error("Update failed:", error);
      alert("Update failed, please retry.");
      throw error;
    }
  };

  const updateHtml = async () => {
    if (htmlUpdating) return;

    try {
      setHtmlUpdating(true);
      await postData(
        "listeningApi/updateHTML",
        { itemId, html, correction_prompt: correctionPrompt },
        navigate
      );
    } catch (error) {
      console.error("Update html failed:", error);
      alert("Failed to update html, please retry.");
    } finally {
      setHtmlUpdating(false);
    }
  };

  const generateCorrection = async () => {
    if (generatingCorrection) return;

    try {
      setGeneratingCorrection(true);
      const res = await postData(
        "listeningApi/processListeningHtmlCheckingPrompt",
        { itemId },
        navigate
      );
      const newCorrection = res?.data;
      if (newCorrection) {
        setCorrectionPrompt(newCorrection);
      }
    } catch (error) {
      console.error("Generate correction failed:", error);
      alert("Failed to generate correction, please retry.");
    } finally {
      setGeneratingCorrection(false);
    }
  };

  const isProcessBtnEnabled = () => {
    const combinedText = `${stText}\n${ndText}`;
    return schoolLevel && combinedText.trim() && isAllSelected() && !processingLoading;
  };
  const processAudioAndSrt = async () => {
    if (processingLoading) return;

    setProcessingLoading(true);
    try {
      await updateVoiceActor();
      const voiceConfig = Object.entries(voiceActorSelections)
        .filter(([_, speaker]) => speaker?.trim() !== '')
        .map(([name, selectedSpeaker]) => {
          // 3. 替换 VoiceList 为 currentVoiceList
          const matchedVoice = currentVoiceList.find(item => item.speaker === selectedSpeaker);
          const speakingRate = matchedVoice ? parseFloat(matchedVoice.speakingRate) : 1.0;
          return {
            name: selectedSpeaker,
            speaker: name,
            languageCode: "en-GB",
            speakingRate: speakingRate
          };
        });

      let requestBody = {
        itemId,
        stText: stText,
        ndText: ndText,
        voiceConfig: voiceConfig
      };

      await postData("listeningApi/processAudioSrt", requestBody, navigate);
      await fetchData();
    } catch (error) {
      console.error("Failed to process audio and SRT:", error);
      alert("Failed to process audio and SRT, please retry.");
    } finally {
      setProcessingLoading(false);
    }
  };
  useEffect(() => {
    fetchDefaultSpeakerVoices();
    if (itemId) fetchData();
  }, [itemId, schoolLevel]);

  return (
    <div className={s.container}>
      {processingLoading && (
        <div className={s.loadingOverlay}>
          <div className={s.loadingContent}>
            <div className={s.spinner}></div>
            <p>Processing Audio and SRT... Please wait.</p>
          </div>
        </div>
      )}

      {/* HTML 编辑区域 */}
      <div className={s.boosterSection} style={{ margin: '20px 0', padding: '15px', border: '1px solid #eee', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <h3 className={s.sectionTitle}>HTML</h3>
        <textarea
          className={s.textAreaAuto}
          rows={10}
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          placeholder="Enter html here..."
          disabled={processingLoading || htmlUpdating}
          style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
        />
        <h3 className={s.sectionTitle}>Correction Prompt</h3>
        <textarea
          className={s.textAreaAuto}
          rows={10}
          value={correctionPrompt}
          onChange={(e) => setCorrectionPrompt(e.target.value)}
          placeholder="Enter correction prompt here..."
          disabled={processingLoading || htmlUpdating}
          style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
        />
        <div>
          <button
            className={s.extractBtn}
            onClick={generateCorrection}
            disabled={processingLoading || generatingCorrection}
            style={{ marginRight: '10px' }}
          >
            {generatingCorrection ? 'Generating...' : 'Generate Correction'}
          </button>
          <button
            className={s.extractBtn}
            onClick={updateHtml}
            disabled={processingLoading || htmlUpdating}
          >
            {htmlUpdating ? 'Updating...' : 'Update Correction Prompt'}
          </button>
        </div>
      </div>

      {/* Extract SRT text button */}
      {(mainMsg || srtNdPath) && (
        <button
          className={s.extractBtn}
          onClick={extractTextFromCurrentSrt}
          disabled={processingLoading}
        >
          extract text from current srt
        </button>
      )}

      {/* 新增：Booster Expression 区域（仅 itemType == 97 显示） */}
      {itemType === 97 && (
        <div className={s.boosterSection} style={{ margin: '20px 0', padding: '15px', border: '1px solid #eee', borderRadius: '8px', display: 'flex', flexDirection: 'column', justifyContent:'center', alignItems:'center' }}>
          <h3 className={s.sectionTitle}>Booster Expression</h3>
          {/* 布局调整：textarea 单独一行，按钮在下方 */}
          <textarea
            className={s.textAreaAuto}
            rows={5}
            value={boosterExpression}
            onChange={(e) => setBoosterExpression(e.target.value)}
            placeholder="Enter booster expression here..."
            disabled={processingLoading || boosterUpdating}
            style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
          />
          <div>
            <button
              className={s.extractBtn}
              onClick={updateBoosterExpression}
              disabled={processingLoading || boosterUpdating || !boosterExpression.trim()}
              style={{ marginBottom: '0' }}
            >
              {boosterUpdating ? 'Updating...' : 'Update Booster Expression'}
            </button>
          </div>
        </div>
      )}

      {/* 修改后：School Level 切换按钮区域 */}
      <div className={s.schoolLevelSection} style={{ margin: '20px 0', padding: '15px', border: '1px solid #eee', borderRadius: '8px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center' }}>
        <h3 className={s.sectionTitle}>School Level</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button
            onClick={() => handleSchoolLevelToggle()}
            disabled={processingLoading || schoolLevelUpdating || !schoolLevel}
            style={{
              padding: '8px 20px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: schoolLevel === 'junior' ? '#e3f2fd' : '#f3e5f5',
              cursor: processingLoading || schoolLevelUpdating || !schoolLevel ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {/* 加载中显示状态 + 加载图标/文字 */}
            {schoolLevelUpdating ? (
              <><span>Updating...</span></>
            ) : (
              <>
                {/* 动态显示当前状态和切换提示 */}
                {schoolLevel === 'junior' ? (
                  <span>Current: Junior (Click to switch to Senior)</span>
                ) : schoolLevel === 'senior' ? (
                  <span>Current: Senior (Click to switch to Junior)</span>
                ) : (
                  <span>Please select initial school level first</span>
                )}
              </>
            )}
          </button>
          {/* 新增：初始化按钮（处理首次无值的情况） */}
          {!schoolLevel && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => updateSchoolLevel('junior')}
                disabled={processingLoading || schoolLevelUpdating}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}
              >
                Set to Junior
              </button>
              <button
                onClick={() => updateSchoolLevel('senior')}
                disabled={processingLoading || schoolLevelUpdating}
                style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#f9f9f9' }}
              >
                Set to Senior
              </button>
            </div>
          )}
        </div>
      </div>

      <h3 className={s.sectionTitle}>Script</h3>

      <div className={s.partContainerHolder}>
        <textarea 
          className={s.textAreaAuto}
          rows={10}
          value={stText}
          onChange={(e) => setStText(e.target.value)}
          placeholder="Paste your stText here (format: Name: Content)..."
          disabled={processingLoading}
        />
        <div>
          <textarea 
            className={s.textAreaAuto}
            rows={10}
            value={ndText}
            onChange={(e) => setNdText(e.target.value)}
            placeholder="Paste your ndText here (format: Name: Content)..."
            disabled={processingLoading}
          />
          <button 
            className={s.extractBtn}
            onClick={breakCurrentText}
            disabled={processingLoading}
          >
            break main text
          </button>
        </div>
      </div>

      {/* Save Script 按钮 */}
      <button 
        className={s.extractBtn}
        onClick={extractName}
        disabled={processingLoading}
      >
        Save Script & Extract Name

      </button>

      {/* 布局调整：Process Audio 按钮移到 Save Script 按钮下方 */}
      <div className={s.btnGroup} style={{ marginBottom: '24px' }}>
        <button 
          className={`${s.processBtn} ${!isProcessBtnEnabled() ? s.processBtnDisabled : ''}`}
          onClick={processAudioAndSrt}
          disabled={!isProcessBtnEnabled()}
        >
          Confirm Voice Actors + Process Audio and SRT
        </button>
      </div>

        {extractedNames.length > 0 && (<div className={s.partContainerHolder}>
        <div className={s.namesListContainer}>
          <h4 className={s.namesTitle}>Assign Voice Actor to Each Name:</h4>
          <ul className={s.namesList}>
            {extractedNames.map((name, index) => (
              <li key={index} className={s.nameItem}>
                <span className={s.nameLabel}>{name}:</span>
                <select 
                  className={s.voiceSelect}
                  value={voiceActorSelections[name] || ''}
                  onChange={(e) => handleVoiceActorChange(name, e.target.value)}
                  disabled={processingLoading || !schoolLevel}
                >
                  <option value="">-- Select Voice Actor --</option>
                  {currentVoiceList.map((voice, idx) => (
                    <option key={idx} value={voice.speaker}>
                      {`${voice.speaker} | ${voice.gender} | ${voice.age}`}
                    </option>
                  ))}
                </select>
                <button 
                  className={s.removeBtn}
                  onClick={() => handleRemoveSpeaker(name)}
                  aria-label={`Remove ${name}`}
                  disabled={processingLoading}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>)}

      {/* 布局调整：Confirm Voice Actors 按钮留在名字列表之后 */}
      <div className={s.partContainerHolder}>
        <div className={s.btnGroup}>
          {extractedNames.length > 0 && (
            <button 
              className={`${s.submitBtn} ${!isAllSelected() ? s.submitBtnDisabled : ''}`}
              onClick={updateVoiceActor}
              disabled={!isAllSelected() || processingLoading}
            >
              Confirm Voice Actors
            </button>
          )}
        </div>
      </div>

      {/* 文件上传区域 */}
      <div className={s.partContainerHolder}>
        <div className={s.partContainer}>
          <div className={s.partCard}>
            <h3 className={s.partTitle}>
              {(itemType == 74|| itemType == 96|| itemType == 97) ? "First Part of listening" : "Podcast"}
            </h3>
            <div className={s.uploadArea}>
              <label htmlFor="srt-upload" className={s.customFileUpload}>
                <input 
                  id="srt-upload" 
                  type="file" 
                  onChange={async (e) => {
                    let path = await generalItemUpload(e.target.files[0]);
                    if (path) updateKeyListeningSrtPath(path);
                  }} 
                />
                Upload srt
              </label>
              
              <label htmlFor="srt-upload-hk" className={s.customFileUpload}>
                <input 
                  id="srt-upload-hk" 
                  type="file" 
                  onChange={async (e) => {
                    let path = await generalItemUpload(e.target.files[0]);
                    if (path) updateKeyListeningSrtHkPath(path);
                  }} 
                />
                Upload HK srt
              </label>
              
              <label htmlFor="audio-upload" className={s.customFileUpload}>
                <input 
                  id="audio-upload" 
                  type="file" 
                  onChange={async (e) => {
                    let path = await generalItemUpload(e.target.files[0]);
                    if (path) updateKeyListeningAudioPath(path);
                  }} 
                />
                Upload audio
              </label>
            </div>
            
            <div className={s.fileDisplayArea}>
              <div className={s.fileCategoryLabel}>article SRT</div>
              {srtPath && (
                <div className={s.fileItem}>
                  <a href={srtPath} download className={s.fileLink}>
                    {extractFileName(srtPath) || srtPath}
                  </a>
                  <button 
                    className={s.deleteBtn}
                    onClick={removeKeyListeningSrtPath}
                    disabled={processingLoading}
                  >
                    X
                  </button>
                </div>
              )}
              
              <div className={s.fileCategoryLabel}>article SRT HK</div>
              {srtHkPath && (
                <div className={s.fileItem}>
                  <a href={srtHkPath} download className={s.fileLink}>
                    {extractFileName(srtHkPath) || srtHkPath}
                  </a>
                  <button 
                    className={s.deleteBtn}
                    onClick={removeKeyListeningSrtHk}
                    disabled={processingLoading}
                  >
                    X
                  </button>
                </div>
              )}
              
              <div className={s.fileCategoryLabel}>mp3 upload</div>
              {audioPath && (
                <div className={s.fileItem}>
                  <div className={s.fileActionGroup}>
                    <SoundPlayer soundFile={audioPath} />
                    <a href={audioPath} download className={s.fileLink}>
                      {extractFileName(audioPath) || audioPath}
                    </a>
                  </div>
                  <button 
                    className={s.deleteBtn}
                    onClick={removeKeyListeningAudioPath}
                    disabled={processingLoading}
                  >
                    X
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {(itemType == 74|| itemType == 96|| itemType == 97) && (
          <div className={s.partContainer}>
            <div className={s.partCard}>
              <h3 className={s.partTitle}>Second Part of listening</h3>
              <div className={s.uploadArea}>
                <label htmlFor="nd-srt-upload" className={s.customFileUpload}>
                  <input 
                    id="nd-srt-upload" 
                    type="file" 
                    onChange={async (e) => {
                      let path = await generalItemUpload(e.target.files[0]);
                      if (path) updateKeyListeningSrtNdPath(path);
                    }} 
                  />
                  Upload srt
                </label>
                
                <label htmlFor="nd-srt-upload-hk" className={s.customFileUpload}>
                  <input 
                    id="nd-srt-upload-hk" 
                    type="file" 
                    onChange={async (e) => {
                      let path = await generalItemUpload(e.target.files[0]);
                      if (path) updateKeyListeningSrtNdHkPath(path);
                    }} 
                  />
                  Upload HK srt
                </label>
                
                <label htmlFor="nd-audio-upload" className={s.customFileUpload}>
                  <input 
                    id="nd-audio-upload" 
                    type="file" 
                    onChange={async (e) => {
                      let path = await generalItemUpload(e.target.files[0]);
                      if (path) updateKeyListeningAudioNdPath(path);
                    }} 
                  />
                  Upload audio
                </label>
              </div>
              
              <div className={s.fileDisplayArea}>
                <div className={s.fileCategoryLabel}>article SRT</div>
                {srtNdPath && (
                  <div className={s.fileItem}>
                    <a href={srtNdPath} download className={s.fileLink}>
                      {extractFileName(srtNdPath) || srtNdPath}
                    </a>
                    <button 
                      className={s.deleteBtn}
                      onClick={removeKeyListeningSrtNdPath}
                      disabled={processingLoading}
                    >
                      X
                    </button>
                  </div>
                )}
                
                <div className={s.fileCategoryLabel}>article HK SRT</div>
                {srtNdHkPath && (
                  <div className={s.fileItem}>
                    <a href={srtNdHkPath} download className={s.fileLink}>
                      {extractFileName(srtNdHkPath) || srtNdHkPath}
                    </a>
                    <button 
                      className={s.deleteBtn}
                      onClick={removeKeyListeningSrtHkNd}
                      disabled={processingLoading}
                    >
                      X
                    </button>
                  </div>
                )}
                
                <div className={s.fileCategoryLabel}>mp3 upload</div>
                {audioNdPath && (
                  <div className={s.fileItem}>
                    <div className={s.fileActionGroup}>
                      <SoundPlayer soundFile={audioNdPath} />
                      <a href={audioNdPath} download className={s.fileLink}>
                        {extractFileName(audioNdPath) || audioNdPath}
                      </a>
                    </div>
                    <button 
                      className={s.deleteBtn}
                      onClick={removeKeyListeningAudioNdPath}
                      disabled={processingLoading}
                    >
                      X
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={s.convertBtnContainer}>
        <button 
          className={s.submitBtn}
          onClick={convertSrt}
          disabled={processingLoading}
        >
          Convert Srt into HK (Just in case)
        </button>
      </div>
    </div>
  );
}